import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Clock4, Flame, Send, Palette, ListChecks, ArrowUpRight } from "lucide-react";

const EDITOR_LINES = [
  "Let's make some bangers today 🎬",
  "Trim, cut, ship. Repeat. ✂️",
  "Today's renders are looking 🔥",
];
const SMM_LINES = [
  "Time to push some content live 🚀",
  "Approve, schedule, slay 💅",
  "Your followers are waiting 📣",
];

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

export default function MyDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [greet] = useState(() => user?.role === "smm" ? pick(SMM_LINES) : pick(EDITOR_LINES));

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/dashboard/me");
        setData(r.data);
      } catch (_) {}
    })();
  }, []);

  if (!data) return <div className="p-10 text-neutral-500 text-sm uppercase tracking-[0.3em]">Loading…</div>;

  if (data.role === "editor") return <EditorView data={data} greet={greet} userName={user?.name} />;
  if (data.role === "smm") return <SMMView data={data} greet={greet} userName={user?.name} />;
  return <div className="p-10 text-neutral-500">No personalized dashboard for this role.</div>;
}

function Tile({ label, value, color, icon: Icon, testId }) {
  return (
    <div className="border border-neutral-900 bg-neutral-950 p-5 hover:bg-neutral-900/40 transition-colors" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} strokeWidth={1.5} />}
      </div>
      <div className="font-heading text-3xl font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}

function TaskList({ title, items, emptyMsg, testId }) {
  return (
    <div className="border border-neutral-900" data-testid={testId}>
      <div className="px-5 py-3 border-b border-neutral-900 text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center justify-between">
        <span>{title}</span>
        <Link to="/schedule" className="text-neutral-500 hover:text-[#00C97A] inline-flex items-center gap-1 text-[10px]">
          Open <ArrowUpRight className="w-2.5 h-2.5" />
        </Link>
      </div>
      <div className="divide-y divide-neutral-900">
        {items.length === 0 && (
          <div className="px-5 py-6 text-sm text-neutral-500 text-center">{emptyMsg}</div>
        )}
        {items.map((t) => (
          <Link key={t.task_id} to={`/schedule?task=${t.task_id}`} className="px-5 py-3 block hover:bg-neutral-900/60 transition-colors">
            <div className="text-sm font-medium truncate">{t.title}</div>
            <div className="text-[10px] text-neutral-500 mt-0.5 font-mono">
              {t.due_date || "no due"} · {t.task_type?.replace(/_/g, " ")}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function EditorView({ data, greet, userName }) {
  const c = data.counts;
  return (
    <div data-testid="editor-dashboard">
      <PageHeader eyebrow="Editor · My Day" title={`Hey ${userName?.split(" ")[0] || "there"} ✏️`} description={greet} />
      <div className="px-6 lg:px-10 py-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-neutral-900 border border-neutral-900">
          <Tile label="In Progress" value={c.in_progress} color="#3B82F6" icon={Flame} testId="ed-in-progress" />
          <Tile label="Pending" value={c.pending} color="#F59E0B" icon={ListChecks} testId="ed-pending" />
          <Tile label="Due Today" value={c.due_today} color="#EF4444" icon={Clock4} testId="ed-due-today" />
          <Tile label="Done This Week" value={c.completed_this_week} color="#00C97A" icon={CheckCircle2} testId="ed-done-week" />
          <Tile label="Active Graphics" value={c.graphics_active} color="#8B5CF6" icon={Palette} testId="ed-gfx" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <TaskList title="Due Today" items={data.due_today} emptyMsg="No deadlines today 🎉" testId="list-due-today" />
          <TaskList title="In Progress" items={data.in_progress} emptyMsg="Nothing in progress" testId="list-in-progress" />
          <TaskList title="Up Next" items={data.pending} emptyMsg="Inbox zero 🥳" testId="list-pending" />
        </div>
      </div>
    </div>
  );
}

function SMMView({ data, greet, userName }) {
  const c = data.counts;
  return (
    <div data-testid="smm-dashboard">
      <PageHeader eyebrow="SMM · My Queue" title={`Hey ${userName?.split(" ")[0] || "there"} 📣`} description={greet} />
      <div className="px-6 lg:px-10 py-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-neutral-900 border border-neutral-900">
          <Tile label="To Approve" value={c.to_approve} color="#F59E0B" icon={ListChecks} testId="smm-to-approve" />
          <Tile label="Ready to Post" value={c.ready_to_post} color="#10B981" icon={Send} testId="smm-ready" />
          <Tile label="Posted Today" value={c.posted_today} color="#00C97A" icon={CheckCircle2} testId="smm-posted-today" />
          <Tile label="Posted · Week" value={c.posted_this_week} color="#E4E4E7" testId="smm-posted-week" />
          <Tile label="Graphics · Review" value={c.graphics_to_review} color="#8B5CF6" icon={Palette} testId="smm-gfx-review" />
          <Tile label="Graphics · Approved" value={c.graphics_approved} color="#10B981" testId="smm-gfx-ok" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TaskList title="Awaiting Your Approval" items={data.to_approve} emptyMsg="Inbox clear ✨" testId="list-to-approve" />
          <TaskList title="Approved · Ready to Post" items={data.ready_to_post} emptyMsg="Nothing queued" testId="list-ready-to-post" />
        </div>
      </div>
    </div>
  );
}
